import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/common/Logo";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { LoadingBlock, EmptyState } from "@/components/common/States";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import api, { humanError } from "@/lib/api";
import { LogOut, LayoutGrid } from "lucide-react";
import TwoFactorSetup from "@/components/security/TwoFactorSetup";
import PlatformTools from "@/components/common/PlatformTools";

const COLORS = ["#1F5FD8", "#16A46B", "#0B2B33", "#E3252A"];

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState(null);
  // The console refuses super admins who have not enrolled a second
  // factor, so that response is a state to render, not an error to toast.
  const [needs2fa, setNeeds2fa] = useState(false);

  const load = async () => {
    try {
      const [o, t, u, a] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/tenants"),
        api.get("/admin/users"),
        api.get("/admin/audit"),
      ]);
      setOverview(o.data); setTenants(t.data.items); setUsers(u.data.items); setAudit(a.data.items);
      setNeeds2fa(false);
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.headers?.["x-2fa-setup-required"]) {
        setNeeds2fa(true);
        return;
      }
      toast.error(humanError(e));
    }
  };
  useEffect(() => { load(); }, []);

  async function setStatus(id, status) {
    if (!window.confirm(`${status === "suspended" ? "Suspend" : "Reactivate"} this tenant?`)) return;
    try { await api.patch(`/admin/tenants/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }
  async function setPlan(id, plan_code) {
    try { await api.patch(`/admin/tenants/${id}`, { plan_code }); toast.success("Plan updated"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }
  async function del(id) {
    if (!window.confirm("Delete this tenant and all its data? Cannot be undone.")) return;
    try { await api.delete(`/admin/tenants/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }

  const k = overview?.kpis || {};
  const dist = (overview?.plan_distribution || []).map(d => ({ ...d, name: d.plan }));

  if (needs2fa) {
    return (
      <div className="min-h-screen bg-[#F6F8FA] px-6 py-16">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <TwoFactorSetup onEnabled={load} />
        <p className="mt-6 text-center text-sm text-[#43535E]">
          <button type="button" onClick={logout} className="font-semibold text-[#1F5FD8] hover:underline">
            Sign out
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FA]">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#E4E9EF] bg-white px-6" data-testid="admin-topbar">
        <div className="flex items-center gap-3">
          <Logo />
          <span className="ml-3 hs-chip bg-[#0B2B33] text-white">Super Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <a href="/app" className="rounded-full border border-[#E4E9EF] px-3 py-1.5 text-sm hover:bg-[#F6F8FA]" data-testid="admin-goto-app"><LayoutGrid className="mr-1 -mt-0.5 inline h-4 w-4" /> Workspaces</a>
          <button onClick={logout} className="rounded-full border border-[#E4E9EF] px-3 py-1.5 text-sm hover:bg-[#F6F8FA]" data-testid="admin-signout"><LogOut className="mr-1 -mt-0.5 inline h-4 w-4" /> Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <PageHeader title="Platform overview" subtitle={`Signed in as ${user?.name}`} />

        {overview === null ? <LoadingBlock rows={4} /> : (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <KpiCard testid="admin-kpi-tenants" label="Tenants" value={k.tenants} hint={`${k.active_tenants} active`} />
              <KpiCard testid="admin-kpi-users" label="Users" value={k.users} />
              <KpiCard testid="admin-kpi-mrr" label="MRR" value={`$${k.mrr_usd?.toLocaleString?.() ?? 0}`} tone="positive" />
              <KpiCard testid="admin-kpi-arr" label="ARR" value={`$${k.arr_usd?.toLocaleString?.() ?? 0}`} />
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              <div className="rounded-xl border border-[#E4E9EF] bg-white p-5 lg:col-span-1">
                <div className="mb-3 text-sm font-semibold text-[#0B2B33]">Plans</div>
                {dist.length === 0 ? <EmptyState title="No tenants" /> :
                  <div className="h-56 min-h-[224px] w-full">
                    <ResponsiveContainer width="100%" height="100%" minHeight={224}>
                      <PieChart>
                        <Pie data={dist} dataKey="count" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                          {dist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                }
              </div>
              <div className="rounded-xl border border-[#E4E9EF] bg-white p-5 lg:col-span-2">
                <div className="mb-3 text-sm font-semibold text-[#0B2B33]">Publish job health</div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                  {["queued","running","succeeded","failed","dead"].map((status) => {
                    const n = overview.system_health?.publish_jobs_by_status?.[status] ?? 0;
                    const tone = status === "succeeded" ? "text-[#0B5D3C] bg-[#E8FBF1]"
                      : status === "failed" || status === "dead" ? "text-[#B02328] bg-[#FFE6E6]"
                      : status === "running" ? "text-[#1747B0] bg-[#EEF3FB]"
                      : "text-[#43535E] bg-[#F6F8FA]";
                    return (
                      <div key={status} className={`rounded-lg px-4 py-3 ${tone}`}>
                        <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{status}</div>
                        <div className="mt-1 font-display text-xl font-bold">{n}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Tabs defaultValue="tenants">
                <TabsList>
                  <TabsTrigger value="tenants" data-testid="admin-tab-tenants">Tenants</TabsTrigger>
                  <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
                  <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
                  <TabsTrigger value="platform" data-testid="admin-tab-platform">Platform</TabsTrigger>
                </TabsList>
                <TabsContent value="tenants" className="mt-4">
                  <div className="rounded-xl border border-[#E4E9EF] bg-white p-5">
                    {tenants === null ? <LoadingBlock rows={3} /> :
                      <table className="w-full text-sm" data-testid="admin-tenants-table">
                        <thead>
                          <tr className="text-left text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
                            <th className="pb-2">Name</th><th className="pb-2">Plan</th><th className="pb-2">Members</th><th className="pb-2">Networks</th><th className="pb-2">Status</th><th></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4E9EF]">
                          {tenants.map(t => (
                            <tr key={t.id} data-testid={`admin-tenant-${t.slug}`}>
                              <td className="py-2 text-[#0B2B33]">{t.name}<div className="text-[11px] text-[#8A97A0]">{t.slug}</div></td>
                              <td className="py-2">
                                <select value={t.plan_code} onChange={(e) => setPlan(t.id, e.target.value)} data-testid={`admin-plan-${t.slug}`} className="rounded-lg border border-[#E4E9EF] px-2 py-1 text-xs capitalize">
                                  {["standard", "professional", "advanced", "enterprise"].map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="py-2 text-[#43535E]">{t.member_count}</td>
                              <td className="py-2 text-[#43535E]">{t.social_count}</td>
                              <td className="py-2">
                                <span className={`hs-chip ${t.status === "active" ? "bg-[#E8FBF1] text-[#0B5D3C]" : "bg-[#FFE6E6] text-[#B02328]"}`}>{t.status}</span>
                              </td>
                              <td className="py-2 text-right">
                                {t.status === "active" ? (
                                  <button onClick={() => setStatus(t.id, "suspended")} data-testid={`admin-suspend-${t.slug}`} className="mr-2 text-xs font-semibold text-[#0B2B33] hover:underline">Suspend</button>
                                ) : (
                                  <button onClick={() => setStatus(t.id, "active")} data-testid={`admin-resume-${t.slug}`} className="mr-2 text-xs font-semibold text-[#16A46B] hover:underline">Reactivate</button>
                                )}
                                <button onClick={() => del(t.id)} data-testid={`admin-delete-${t.slug}`} className="text-xs font-semibold text-[#E3252A] hover:underline">Delete</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    }
                  </div>
                </TabsContent>
                <TabsContent value="users" className="mt-4">
                  <div className="rounded-xl border border-[#E4E9EF] bg-white p-5">
                    {users === null ? <LoadingBlock rows={3} /> :
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
                            <th className="pb-2">Name</th><th className="pb-2">Email</th><th className="pb-2">Tenants</th><th className="pb-2">Platform</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4E9EF]">
                          {users.map(u => (
                            <tr key={u.id}>
                              <td className="py-2 text-[#0B2B33]">{u.name}</td>
                              <td className="py-2 text-[#43535E]">{u.email}</td>
                              <td className="py-2 text-[#43535E]">{u.tenants.map(t => `${t.tenant_name} (${t.role})`).join(", ") || "—"}</td>
                              <td className="py-2">{u.is_super_admin ? <span className="hs-chip bg-[#0B2B33] text-white">Super Admin</span> : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    }
                  </div>
                </TabsContent>
                <TabsContent value="audit" className="mt-4">
                  <div className="rounded-xl border border-[#E4E9EF] bg-white p-5">
                    {audit === null ? <LoadingBlock rows={4} /> :
                      audit.length === 0 ? <EmptyState title="No events yet" /> :
                        <ul className="space-y-2">
                          {audit.map(a => (
                            <li key={a.id} className="rounded-lg bg-[#F6F8FA] p-3 text-xs">
                              <span className="font-semibold text-[#0B2B33]">{a.action}</span>
                              {a.target_kind ? <span className="ml-1 text-[#43535E]">· {a.target_kind} {a.target_id?.slice(0, 8)}</span> : null}
                              <span className="float-right text-[#8A97A0]">{new Date(a.at).toLocaleString()}</span>
                            </li>
                          ))}
                        </ul>
                    }
                  </div>
                </TabsContent>
                <TabsContent value="platform" className="mt-4">
                  <PlatformTools tenants={tenants} onChanged={load} />
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
